import React, { useState, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, fmtUSD, fmtPct } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toPng } from "html-to-image";
import { PageHeader } from "@/components/Bits";
import { Download, Twitter, Copy, Activity, TrendingUp, Sparkles, Eye, EyeOff, Flame } from "lucide-react";
import { toast } from "sonner";
import { ResponsiveContainer, AreaChart, Area } from "recharts";

const PERIODS = [
  { id: "week", label: "LAST 7 DAYS" },
  { id: "month", label: "LAST 30 DAYS" },
  { id: "year", label: "LAST YEAR" },
  { id: "all", label: "ALL TIME" },
];

export default function TraderCard() {
  const { user } = useAuth();
  const cardRef = useRef(null);
  const [period, setPeriod] = useState("month");
  const [anonymous, setAnonymous] = useState(false);
  const [exporting, setExporting] = useState(false);

  const { data: stats } = useQuery({
    queryKey: ["stats", period],
    queryFn: async () => (await api.get(`/stats/overview?period=${period}`)).data,
  });

  const { data: streak } = useQuery({
    queryKey: ["recap-streak"],
    queryFn: async () => (await api.get("/recap/streak")).data,
  });

  const exportPng = async () => {
    if (!cardRef.current) return;
    setExporting(true);
    try {
      const dataUrl = await toPng(cardRef.current, {
        pixelRatio: 2,
        backgroundColor: "#09090b",
        cacheBust: true,
        skipFonts: true,
      });
      const link = document.createElement("a");
      link.download = `tradegreen-${period}-${new Date().toISOString().slice(0, 10)}.png`;
      link.href = dataUrl;
      link.click();
      toast.success("CARD DOWNLOADED");
    } catch (e) {
      toast.error("Export failed");
    } finally { setExporting(false); }
  };

  const copyImg = async () => {
    if (!cardRef.current) return;
    try {
      const dataUrl = await toPng(cardRef.current, { pixelRatio: 2, backgroundColor: "#09090b", cacheBust: true });
      const blob = await (await fetch(dataUrl)).blob();
      await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
      toast.success("COPIED TO CLIPBOARD · paste it anywhere");
    } catch {
      toast.error("Clipboard not supported — use Download");
    }
  };

  const tweet = () => {
    const text = encodeURIComponent(
      `My ${period === "all" ? "all-time" : `last ${period === "year" ? "year" : period === "month" ? "30 days" : "week"}`} on @tradegreen:\n` +
      `P&L ${fmtUSD(stats?.total_pnl || 0)} · Win rate ${fmtPct(stats?.win_rate || 0)} · PF ${(stats?.profit_factor || 0).toFixed(2)}\n` +
      `Sharpen yours → tradegreen.app`
    );
    window.open(`https://twitter.com/intent/tweet?text=${text}`, "_blank");
  };

  const displayName = anonymous ? "ANONYMOUS TRADER" : (user?.name || user?.email || "TRADER").toUpperCase();
  const periodLabel = PERIODS.find((p) => p.id === period)?.label;
  const pnlPositive = (stats?.total_pnl || 0) >= 0;

  return (
    <div>
      <PageHeader eyebrow="GROWTH" title="Shareable Trader Card" />

      <div className="p-8 grid lg:grid-cols-[1fr_320px] gap-8">
        {/* Card preview */}
        <div className="flex justify-center">
          <div ref={cardRef} className="w-[680px] bg-[color:var(--bg)] border border-[color:var(--border)] relative overflow-hidden" data-testid="trader-card">
            {/* Background flair */}
            <div className="absolute inset-0 opacity-50 pointer-events-none"
              style={{
                backgroundImage: "linear-gradient(to right, rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.025) 1px, transparent 1px)",
                backgroundSize: "32px 32px",
              }} />
            <div className="absolute inset-0 bg-gradient-to-br from-sky-500/8 via-transparent to-transparent pointer-events-none" />

            <div className="relative p-8">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <Activity className="h-4 w-4 text-[color:var(--green)]" strokeWidth={1.5} />
                  <span className="font-heading text-sm tracking-tight">TRADE<span className="text-[color:var(--green)]">GREEN</span></span>
                </div>
                <div className="text-[10px] font-mono-num text-[color:var(--fg-3)] tracking-widest">{periodLabel}</div>
              </div>

              {/* Trader name */}
              <div className="mb-8">
                <div className="text-[10px] font-mono-num text-[color:var(--fg-3)] tracking-widest">TRADER</div>
                <div className="font-heading text-2xl text-[color:var(--fg)] mt-1 tracking-tight">{displayName}</div>
              </div>

              {/* Hero P&L */}
              <div className="mb-6">
                <div className="text-[10px] font-mono-num text-[color:var(--fg-3)] tracking-widest">NET P&L</div>
                <div className={`font-mono-num text-6xl font-light mt-1 ${pnlPositive ? "text-[color:var(--green)]" : "text-[color:var(--loss)]"}`}>
                  {pnlPositive && "+"}{fmtUSD(stats?.total_pnl || 0)}
                </div>
              </div>

              {/* Stats grid */}
              <div className="grid grid-cols-4 gap-0 border border-[color:var(--border)] [&>*]:border-r [&>*]:border-[color:var(--border)] [&>*:last-child]:border-r-0 mb-6">
                <div className="p-3">
                  <div className="text-[9px] font-mono-num text-[color:var(--fg-3)] tracking-widest">WIN RATE</div>
                  <div className="font-mono-num text-lg text-[color:var(--fg)] mt-1">{fmtPct(stats?.win_rate || 0)}</div>
                </div>
                <div className="p-3">
                  <div className="text-[9px] font-mono-num text-[color:var(--fg-3)] tracking-widest">PF</div>
                  <div className="font-mono-num text-lg text-[color:var(--fg)] mt-1">{(stats?.profit_factor || 0).toFixed(2)}</div>
                </div>
                <div className="p-3">
                  <div className="text-[9px] font-mono-num text-[color:var(--fg-3)] tracking-widest">TRADES</div>
                  <div className="font-mono-num text-lg text-[color:var(--fg)] mt-1">{stats?.total_trades || 0}</div>
                </div>
                <div className="p-3">
                  <div className="text-[9px] font-mono-num text-[color:var(--fg-3)] tracking-widest">SHARPE</div>
                  <div className="font-mono-num text-lg text-[color:var(--fg)] mt-1">{(stats?.sharpe || 0).toFixed(2)}</div>
                </div>
              </div>

              {/* Mini equity curve */}
              {stats?.equity_curve?.length > 1 && (
                <div className="h-20 -mx-2 mb-4">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={stats.equity_curve}>
                      <defs>
                        <linearGradient id="card-eq" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={pnlPositive ? "#34d399" : "#fb7185"} stopOpacity={0.4} />
                          <stop offset="100%" stopColor={pnlPositive ? "#34d399" : "#fb7185"} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <Area type="monotone" dataKey="equity" stroke={pnlPositive ? "#34d399" : "#fb7185"} strokeWidth={2} fill="url(#card-eq)" isAnimationActive={false} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Footer */}
              <div className="flex items-center justify-between pt-4 border-t border-[color:var(--border)] text-[10px] font-mono-num">
                <div className="flex items-center gap-4 text-[color:var(--fg-3)]">
                  {streak?.streak > 0 && (
                    <span className="flex items-center gap-1.5 text-[color:var(--green)]">
                      <Flame className="h-3 w-3" strokeWidth={1.5} /> {streak.streak}-DAY RECAP STREAK
                    </span>
                  )}
                </div>
                <div className="text-[color:var(--fg-3)]">
                  TRACK YOURS · <span className="text-[color:var(--green)]">tradegreen.app</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="space-y-4">
          <div className="border border-[color:var(--border)] p-4">
            <div className="label-eyebrow mb-3">RANGE</div>
            <div className="grid grid-cols-2 gap-1">
              {PERIODS.map((p) => (
                <button key={p.id} onClick={() => setPeriod(p.id)} data-testid={`card-period-${p.id}`}
                  className={`px-3 py-2 text-xs font-mono-num transition-colors ${period === p.id ? "bg-[color:var(--accent)] text-black" : "bg-[color:var(--surface)] text-[color:var(--fg-2)] hover:text-[color:var(--fg)]"}`}>
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <div className="border border-[color:var(--border)] p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="label-eyebrow">PRIVACY</div>
                <div className="text-xs text-[color:var(--fg-2)] mt-1">Hide your name in the card</div>
              </div>
              <button onClick={() => setAnonymous(!anonymous)} data-testid="anonymous-toggle"
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs border transition-colors ${anonymous ? "bg-[rgba(168,85,247,0.12)] text-[color:var(--accent)] border-[color:var(--accent)]" : "bg-[color:var(--surface)] text-[color:var(--fg-2)] border-[color:var(--border)]"}`}>
                {anonymous ? <EyeOff className="h-3 w-3" strokeWidth={1.5} /> : <Eye className="h-3 w-3" strokeWidth={1.5} />}
                {anonymous ? "ANON" : "PUBLIC"}
              </button>
            </div>
          </div>

          <div className="border border-[color:var(--border)] p-4 space-y-2">
            <div className="label-eyebrow mb-2">EXPORT & SHARE</div>
            <button onClick={exportPng} disabled={exporting} data-testid="download-png-btn"
              className="w-full flex items-center justify-center gap-2 bg-[color:var(--accent)] text-black hover:bg-[color:var(--accent)] transition-colors px-4 py-2.5 text-sm disabled:opacity-50">
              <Download className="h-4 w-4" strokeWidth={1.5} /> {exporting ? "GENERATING…" : "DOWNLOAD PNG"}
            </button>
            <button onClick={copyImg} data-testid="copy-img-btn"
              className="w-full flex items-center justify-center gap-2 bg-[color:var(--surface)] border border-[color:var(--border)] text-[color:var(--fg-2)] hover:bg-[color:var(--surface2)] transition-colors px-4 py-2 text-sm">
              <Copy className="h-3.5 w-3.5" strokeWidth={1.5} /> COPY IMAGE TO CLIPBOARD
            </button>
            <button onClick={tweet} data-testid="tweet-btn"
              className="w-full flex items-center justify-center gap-2 bg-[color:var(--surface)] border border-[color:var(--border)] text-[color:var(--fg-2)] hover:bg-[color:var(--surface2)] transition-colors px-4 py-2 text-sm">
              <Twitter className="h-3.5 w-3.5" strokeWidth={1.5} /> COMPOSE TWEET
            </button>
          </div>

          <div className="border border-[color:var(--accent)]/30 bg-[rgba(168,85,247,0.05)] p-4">
            <div className="label-eyebrow text-[color:var(--accent)] mb-2 flex items-center gap-1.5">
              <Sparkles className="h-3 w-3" strokeWidth={1.5} /> VIRAL LOOP
            </div>
            <p className="text-xs text-[color:var(--fg-2)] leading-relaxed">
              Every share includes a subtle <span className="text-[color:var(--accent)]">tradegreen.app</span> footer. When your followers see your week, they know exactly where you tracked it.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
